import moment from 'moment';

import { MARKET_DATA_DATE } from '../../lib/constants/date-time.constants';

import { DateRangeState } from '../../util/types';

import { DateRangeDropdownOptionEnum } from './DateRangePickerWrapper.types';

export const DATE_RANGE_KEY = 'selection';

const ONE_DAY = 1;
const ONE_WEEK_DAYS = 7;
const ONE_MONTH_DAYS = 30;
const ONE_WEEK_END_OFFSET_DAYS = ONE_WEEK_DAYS - ONE_DAY;
const ONE_MONTH_END_OFFSET_DAYS = ONE_MONTH_DAYS - ONE_DAY;
const MIN_ONE_MONTH_RANGE_DIFF_DAYS = 29;
const MAX_ONE_MONTH_RANGE_DIFF_DAYS = 31;

const getBaseDate = (baseDate?: Date | string): moment.Moment => moment(baseDate ?? new Date()).startOf('day');

const createRange = (startDate?: Date, endDate?: Date): DateRangeState => ({
  startDate,
  endDate,
  key: DATE_RANGE_KEY,
});

export const formatDateToMarketData = (date?: Date | string): string | undefined => (
  date ? moment(date).format(MARKET_DATA_DATE) : undefined
);

export const formatInputDateValue = (date?: Date | string): string | undefined => (
  date ? moment(date).format(MARKET_DATA_DATE) : undefined
);

type ResolveDateRangeFromPropsParams = {
  minDateState?: string;
  maxDateState?: string;
  initialState: DateRangeState;
  withDateRangeDropdown?: boolean;
  dropdownBaseDate?: Date | string;
}

export const resolveDateRangeFromProps = ({
  minDateState,
  maxDateState,
  initialState,
  withDateRangeDropdown = false,
  dropdownBaseDate,
}: ResolveDateRangeFromPropsParams): DateRangeState => {
  const fallbackDropdownRange = withDateRangeDropdown
    ? getDateRangeByDropdownOption(DateRangeDropdownOptionEnum.OneMonth, dropdownBaseDate)
    : createRange();

  const startDate = minDateState
    ? new Date(minDateState)
    : (initialState?.startDate ?? fallbackDropdownRange.startDate);
  const endDate = maxDateState
    ? new Date(maxDateState)
    : (initialState?.endDate ?? fallbackDropdownRange.endDate);

  return {
    startDate,
    endDate,
    key: DATE_RANGE_KEY,
  };
};

export type DateRangeSyncSnapshot = {
  startDate?: string;
  endDate?: string;
}

export const createDateRangeSyncSnapshot = (
  startDate?: Date | string,
  endDate?: Date | string,
): DateRangeSyncSnapshot => ({
  startDate: formatInputDateValue(startDate),
  endDate: formatInputDateValue(endDate),
});

export const isDateRangeSyncSnapshotChanged = (
  previousSnapshot: DateRangeSyncSnapshot,
  nextSnapshot: DateRangeSyncSnapshot,
): boolean => (
  previousSnapshot.startDate !== nextSnapshot.startDate
  || previousSnapshot.endDate !== nextSnapshot.endDate
);

export const getDateRangeByDropdownOption = (
  option: DateRangeDropdownOptionEnum,
  baseDate?: Date | string,
): DateRangeState => {
  const baseDateMoment = getBaseDate(baseDate);

  if (option === DateRangeDropdownOptionEnum.Tomorrow) {
    const tomorrow = baseDateMoment.clone().add(ONE_DAY, 'day').toDate();
    return createRange(tomorrow, tomorrow);
  }

  if (option === DateRangeDropdownOptionEnum.OneWeek) {
    const startDate = baseDateMoment.toDate();
    const endDate = baseDateMoment.clone().add(ONE_WEEK_END_OFFSET_DAYS, 'day').toDate();
    return createRange(startDate, endDate);
  }

  if (option === DateRangeDropdownOptionEnum.OneMonth) {
    const startDate = baseDateMoment.toDate();
    const endDate = baseDateMoment.clone().add(ONE_MONTH_END_OFFSET_DAYS, 'day').toDate();
    return createRange(startDate, endDate);
  }

  return createRange();
};

export const resolveDateRangeDropdownOption = (
  startDate?: Date | string,
  endDate?: Date | string,
  baseDate?: Date | string,
): DateRangeDropdownOptionEnum => {
  if (!startDate || !endDate) {
    return DateRangeDropdownOptionEnum.OneMonth;
  }

  const startDateMoment = moment(startDate).startOf('day');
  const endDateMoment = moment(endDate).startOf('day');
  const baseDateMoment = getBaseDate(baseDate);

  if (!startDateMoment.isValid() || !endDateMoment.isValid()) {
    return DateRangeDropdownOptionEnum.CustomDate;
  }

  const diffDays = endDateMoment.diff(startDateMoment, 'days');
  const isStartToday = startDateMoment.isSame(baseDateMoment, 'day');
  const tomorrowDate = baseDateMoment.clone().add(ONE_DAY, 'day');
  const isTomorrow = startDateMoment.isSame(tomorrowDate, 'day') && endDateMoment.isSame(tomorrowDate, 'day');

  if (isTomorrow) {
    return DateRangeDropdownOptionEnum.Tomorrow;
  }

  if (isStartToday && diffDays === ONE_WEEK_END_OFFSET_DAYS) {
    return DateRangeDropdownOptionEnum.OneWeek;
  }

  if (isStartToday && diffDays >= MIN_ONE_MONTH_RANGE_DIFF_DAYS && diffDays <= MAX_ONE_MONTH_RANGE_DIFF_DAYS) {
    return DateRangeDropdownOptionEnum.OneMonth;
  }

  return DateRangeDropdownOptionEnum.CustomDate;
};
